import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { getSiteContent, updateSiteContent, getPartners, createPartner, updatePartner, deletePartner } from '../../api/api';
import { FileUpload } from '../../components/FileUpload';

export const AdminPages = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [partners, setPartners] = useState([]);
  const [editingPartner, setEditingPartner] = useState(null);
  const [newPartner, setNewPartner] = useState({ name: '', description: '', logo: '' });
  const [contentData, setContentData] = useState({
    contactTitle: '',
    contactSubtitle: '',
    contactEmail: '',
    contactPhone: '',
    contactAddress: '',
    aboutTitle: '',
    aboutSubtitle: '',
    aboutDescription: '',
    aboutStoryTitle: '',
    aboutStoryContent: '',
    partnersTitle: '',
    partnersSubtitle: ''
  });
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [content, partnersData] = await Promise.all([
        getSiteContent(),
        getPartners()
      ]);
      setContentData({
        contactTitle: content.contactTitle || '',
        contactSubtitle: content.contactSubtitle || '',
        contactEmail: content.contactEmail || '',
        contactPhone: content.contactPhone || '',
        contactAddress: content.contactAddress || '',
        aboutTitle: content.aboutTitle || '',
        aboutSubtitle: content.aboutSubtitle || '',
        aboutDescription: content.aboutDescription || '',
        aboutStoryTitle: content.aboutStoryTitle || '',
        aboutStoryContent: content.aboutStoryContent || '',
        partnersTitle: content.partnersTitle || '',
        partnersSubtitle: content.partnersSubtitle || ''
      });
      setPartners(partnersData);
    } catch (error) {
      toast.error("Impossible de charger les données");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveContent = async () => {
    setIsSaving(true);
    try {
      await updateSiteContent(contentData);
      toast.success("Contenu mis à jour avec succès !");
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddPartner = async () => {
    if (!newPartner.name || !newPartner.description) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    try {
      await createPartner(newPartner);
      setNewPartner({ name: '', description: '', logo: '' });
      toast.success("Partenaire ajouté avec succès !");
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'ajout");
    }
  };

  const handleUpdatePartner = async (partnerId, data) => {
    try {
      await updatePartner(partnerId, data);
      setEditingPartner(null);
      toast.success("Partenaire mis à jour avec succès !");
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Erreur lors de la mise à jour");
    }
  };

  const handleDeletePartner = async (partnerId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire ?')) return;
    try {
      await deletePartner(partnerId);
      toast.success("Partenaire supprimé avec succès !");
      await fetchData();
    } catch (error) {
      toast.error(error.message || "Erreur lors de la suppression");
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF1B6D]"></div></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Gestion des Pages</h2>
        <Button onClick={handleSaveContent} disabled={isSaving} className="bg-[#FF1B6D] hover:bg-[#E01660] text-white">
          <Save size={18} className="mr-2" />{isSaving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('contact')}
          className={`pb-3 px-4 font-semibold transition-colors ${
            activeTab === 'contact' ? 'border-b-2 border-[#FF1B6D] text-[#FF1B6D]' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Page Contact
        </button>
        <button
          onClick={() => setActiveTab('about')}
          className={`pb-3 px-4 font-semibold transition-colors ${
            activeTab === 'about' ? 'border-b-2 border-[#FF1B6D] text-[#FF1B6D]' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Page À Propos
        </button>
        <button
          onClick={() => setActiveTab('partners')}
          className={`pb-3 px-4 font-semibold transition-colors ${
            activeTab === 'partners' ? 'border-b-2 border-[#FF1B6D] text-[#FF1B6D]' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Page Partenaires
        </button>
      </div>

      {/* Contact Tab */}
      {activeTab === 'contact' && (
        <Card>
          <CardHeader><CardTitle>Contenu de la Page Contact</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="contactTitle">Titre de la page</Label>
              <Input
                id="contactTitle"
                value={contentData.contactTitle}
                onChange={(e) => setContentData({ ...contentData, contactTitle: e.target.value })}
                placeholder="CONTACTEZ-NOUS"
              />
            </div>
            <div>
              <Label htmlFor="contactSubtitle">Sous-titre</Label>
              <Textarea
                id="contactSubtitle"
                value={contentData.contactSubtitle}
                onChange={(e) => setContentData({ ...contentData, contactSubtitle: e.target.value })}
                rows={2}
                placeholder="Vous avez des questions ? N'hésitez pas à nous contacter..."
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contactEmail">Email de contact *</Label>
                <Input
                  id="contactEmail"
                  type="email"
                  value={contentData.contactEmail}
                  onChange={(e) => setContentData({ ...contentData, contactEmail: e.target.value })}
                  placeholder="contact@example.com"
                />
              </div>
              <div>
                <Label htmlFor="contactPhone">Téléphone</Label>
                <Input
                  id="contactPhone"
                  value={contentData.contactPhone}
                  onChange={(e) => setContentData({ ...contentData, contactPhone: e.target.value })}
                  placeholder="+33 6 XX XX XX XX"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contactAddress">Adresse</Label>
              <Input
                id="contactAddress"
                value={contentData.contactAddress}
                onChange={(e) => setContentData({ ...contentData, contactAddress: e.target.value })}
                placeholder="Ain, France"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* About Tab */}
      {activeTab === 'about' && (
        <Card>
          <CardHeader><CardTitle>Contenu de la Page À Propos</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="aboutTitle">Titre principal</Label>
              <Input
                id="aboutTitle"
                value={contentData.aboutTitle}
                onChange={(e) => setContentData({ ...contentData, aboutTitle: e.target.value })}
                placeholder="À Propos de Nous"
              />
            </div>
            <div>
              <Label htmlFor="aboutSubtitle">Sous-titre</Label>
              <Textarea
                id="aboutSubtitle"
                value={contentData.aboutSubtitle}
                onChange={(e) => setContentData({ ...contentData, aboutSubtitle: e.target.value })}
                rows={2}
                placeholder="Nous sommes une équipe passionnée..."
              />
            </div>
            <div>
              <Label htmlFor="aboutDescription">Description principale</Label>
              <Textarea
                id="aboutDescription"
                value={contentData.aboutDescription}
                onChange={(e) => setContentData({ ...contentData, aboutDescription: e.target.value })}
                rows={4}
                placeholder="Décrivez votre mission et vos valeurs..."
              />
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-semibold text-gray-900 mb-4">Section "Notre Histoire"</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="aboutStoryTitle">Titre</Label>
                  <Input
                    id="aboutStoryTitle"
                    value={contentData.aboutStoryTitle}
                    onChange={(e) => setContentData({ ...contentData, aboutStoryTitle: e.target.value })}
                    placeholder="Notre Histoire"
                  />
                </div>
                <div>
                  <Label htmlFor="aboutStoryContent">Contenu (Utilisez \n\n pour séparer les paragraphes)</Label>
                  <Textarea
                    id="aboutStoryContent"
                    value={contentData.aboutStoryContent}
                    onChange={(e) => setContentData({ ...contentData, aboutStoryContent: e.target.value })}
                    rows={8}
                    placeholder="Racontez votre histoire..."
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle>En-tête de la Page Partenaires</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="partnersTitle">Titre</Label>
                <Input
                  id="partnersTitle"
                  value={contentData.partnersTitle}
                  onChange={(e) => setContentData({ ...contentData, partnersTitle: e.target.value })}
                  placeholder="Nos Partenaires"
                />
              </div>
              <div>
                <Label htmlFor="partnersSubtitle">Sous-titre</Label>
                <Input
                  id="partnersSubtitle"
                  value={contentData.partnersSubtitle}
                  onChange={(e) => setContentData({ ...contentData, partnersSubtitle: e.target.value })}
                  placeholder="Merci à nos partenaires qui soutiennent la culture urbaine"
                />
              </div>
            </CardContent>
          </Card>

          {/* Add New Partner */}
          <Card>
            <CardHeader><CardTitle>Ajouter un Partenaire</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du partenaire *</Label>
                    <Input
                      value={newPartner.name}
                      onChange={(e) => setNewPartner({ ...newPartner, name: e.target.value })}
                      placeholder="Nom du partenaire"
                    />
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Input
                      value={newPartner.description}
                      onChange={(e) => setNewPartner({ ...newPartner, description: e.target.value })}
                      placeholder="Soutien municipal, Partenaire culturel..."
                    />
                  </div>
                </div>
                <FileUpload
                  value={newPartner.logo}
                  onChange={(url) => setNewPartner({ ...newPartner, logo: url })}
                  accept="image/*"
                  label="Logo du partenaire (optionnel)"
                />
              </div>
              <Button onClick={handleAddPartner} className="bg-[#FF1B6D] hover:bg-[#E01660] text-white">
                <Plus size={18} className="mr-2" />Ajouter le partenaire
              </Button>
            </CardContent>
          </Card>

          {/* Partners List */}
          <Card>
            <CardHeader><CardTitle>Liste des Partenaires ({partners.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {partners.map((partner) => (
                  <div key={partner.id} className="p-4 border rounded-lg flex items-start justify-between">
                    {editingPartner?.id === partner.id ? (
                      <div className="flex-1 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            value={editingPartner.name}
                            onChange={(e) => setEditingPartner({ ...editingPartner, name: e.target.value })}
                            placeholder="Nom"
                          />
                          <Input
                            value={editingPartner.description}
                            onChange={(e) => setEditingPartner({ ...editingPartner, description: e.target.value })}
                            placeholder="Description"
                          />
                        </div>
                        <FileUpload
                          value={editingPartner.logo || ''}
                          onChange={(url) => setEditingPartner({ ...editingPartner, logo: url })}
                          accept="image/*"
                          label="Logo du partenaire"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleUpdatePartner(partner.id, editingPartner)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            Enregistrer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPartner(null)}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-4 flex-1">
                          {partner.logo ? (
                            <img src={partner.logo} alt={partner.name} className="w-12 h-12 object-contain rounded border" />
                          ) : (
                            <div className="w-12 h-12 bg-[#FF1B6D] rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">{partner.name.charAt(0)}</span>
                            </div>
                          )}
                          <div>
                            <h4 className="font-bold text-gray-900">{partner.name}</h4>
                            <p className="text-gray-600 text-sm">{partner.description}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingPartner(partner)}
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeletePartner(partner.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {partners.length === 0 && (
                  <p className="text-gray-500 text-center py-8">Aucun partenaire pour le moment</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
